import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ADMIN_API_URL, USER_TYPE_OPTIONS } from '../../constants';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import showToast from '../../utils/toast';

const RoleManagementPage = ({ addNotification }) => {
    const navigate = useNavigate();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const tokenExpirationHandledRef = useRef(false);

    // Helper function to check for token expiration
    const handleTokenExpiration = (error) => {
        // If already handled, skip to avoid duplicate notifications (synchronous check with useRef)
        if (tokenExpirationHandledRef.current) {
            return true;
        }

        if (error && error.name === "TokenExpiredError" || 
            (error.message && error.message.includes("jwt expired")) ||
            (typeof error === 'object' && error.message && error.message.name === "TokenExpiredError")) {
            // Mark as handled immediately (synchronous) to prevent duplicate calls
            tokenExpirationHandledRef.current = true;
            showToast("Session expired. Please login again.", 'warning');
            addNotification("Session expired. Please login again.", 'warning');
            // Clear session storage
            sessionStorage.removeItem("authToken");
            sessionStorage.removeItem("userInfo");
            // Redirect to login
            setTimeout(() => {
                navigate("/");
            }, 2000);
            return true;
        }
        return false;
    };
    const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
    const [showEditRoleModal, setShowEditRoleModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [allRolesForParent, setAllRolesForParent] = useState([]); // All roles for parent dropdown
    const [newRole, setNewRole] = useState({
        roleName: '',
        roleDescription: '',
        status: 'Active',
        userType: 0,
        parentRoleId: '', // Parent role for hierarchy
        assignedUserIds: [],
        permissions: []
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
    const [needsReassignment, setNeedsReassignment] = useState(false);
    const [userCount, setUserCount] = useState(0);
    const [availableRoles, setAvailableRoles] = useState([]);
    const [selectedRoleId, setSelectedRoleId] = useState('');

    useEffect(() => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found. Please log in again.");
            window.location.href = "/";
            return;
        }
        fetchRoles();
        fetchPermissions();
        fetchAllRolesForParent(); // Fetch all roles for parent dropdown
    }, [currentPage, recordsPerPage]);

    const fetchRoles = async () => {
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found.");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}roles?page=${currentPage}&limit=${recordsPerPage}`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`
                }
            });

            const result = await response.json();
            
            // Check for token expiration
            if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
                if (handleTokenExpiration(result.message)) {
                    setLoading(false);
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(result.message || "Failed to fetch roles.");
            }

            setRoles(result.data);
            setTotalRecords(result.pagination.totalRecords);
            setTotalPages(result.pagination.totalPages);
        } catch (err) {
            // Check for token expiration in error
            if (handleTokenExpiration(err)) {
                setLoading(false);
                return;
            }
            setError(err.message);
            setRoles([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchPermissions = async () => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            // Error handled by fetchRoles or separate login redirect
            return;
        }
        try {
            const response = await fetch(`${ADMIN_API_URL}permissions`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`
                }
            });

            const result = await response.json();
            
            // Check for token expiration
            if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
                if (handleTokenExpiration(result.message)) {
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(result.message || "Failed to fetch permissions.");
            }

            setPermissions(result.data);
        } catch (err) {
            // Check for token expiration in error
            if (handleTokenExpiration(err)) {
                return;
            }
            console.error("Error fetching permissions:", err);
            // Optionally add notification
        }
    };

    const fetchAllRolesForParent = async () => {
        const token = sessionStorage.getItem("authToken");
        if (!token) return;
        try {
            // Fetch all roles without pagination for parent dropdown
            const response = await fetch(`${ADMIN_API_URL}roles?page=1&limit=1000`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`
                }
            });

            const result = await response.json();
            
            // Check for token expiration
            if (result.message && (result.message.name === "TokenExpiredError" || result.message.message === "jwt expired")) {
                if (handleTokenExpiration(result.message)) {
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(result.message || "Failed to fetch roles for parent.");
            }

            setAllRolesForParent(result.data || []);
        } catch (err) {
            // Check for token expiration in error
            if (handleTokenExpiration(err)) {
                return;
            }
            console.error("Error fetching roles for parent:", err);
        }
    };

    const handleCreateRole = async () => {
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found.");
            setLoading(false);
            return;
        }

        // Validate userType: must be 0, 1, or 2
        const userType = typeof newRole.userType === 'string' 
            ? USER_TYPE_OPTIONS.find(opt => opt.label === newRole.userType)?.value 
            : newRole.userType;
        
        if (userType !== 0 && userType !== 1 && userType !== 2) {
            setError("Invalid userType. Must be one of: 0 (Employee), 1 (Anchor Company), or 2 (Lead Channel Partner).");
            addNotification("Invalid userType. Must be one of: 0 (Employee), 1 (Anchor Company), or 2 (Lead Channel Partner).", 'error');
            setLoading(false);
            return;
        }

        const rolePayload = {
            ...newRole,
            userType: userType,
            parentRoleId: newRole.parentRoleId || null // Convert empty string to null
        };

        try {
            const response = await fetch(`${ADMIN_API_URL}roles`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`
                },
                body: JSON.stringify(rolePayload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create role.");
            }

            addNotification("Role created successfully.", 'success');
            setShowCreateRoleModal(false);
            setNewRole({
                roleName: '',
                roleDescription: '',
                status: 'Active',
                userType: 0,
                parentRoleId: '',
                assignedUserIds: [],
                permissions: []
            });
            fetchRoles(); // Refresh roles list
            fetchAllRolesForParent(); // Refresh parent roles list
        } catch (err) {
            setError(err.message);
            addNotification(`Error: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateRole = async () => {
        if (!selectedRole || !selectedRole.id) {
            console.error('No role selected or missing role ID');
            return;
        }

        console.log('Starting role update...', selectedRole);
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found.");
            setLoading(false);
            return;
        }

        // Validate userType: must be 0, 1, or 2
        // If userType is undefined or null, default to 0
        let userType;
        if (selectedRole.userType === undefined || selectedRole.userType === null) {
            userType = 0; // Default to Employee
        } else if (typeof selectedRole.userType === 'string') {
            userType = USER_TYPE_OPTIONS.find(opt => opt.label === selectedRole.userType)?.value ?? 0;
        } else {
            userType = selectedRole.userType;
        }
        
        if (userType !== 0 && userType !== 1 && userType !== 2) {
            console.error('Invalid userType:', userType, 'from selectedRole.userType:', selectedRole.userType);
            setError("Invalid userType. Must be one of: 0 (Employee), 1 (Anchor Company), or 2 (Lead Channel Partner).");
            addNotification("Invalid userType. Must be one of: 0 (Employee), 1 (Anchor Company), or 2 (Lead Channel Partner).", 'error');
            setLoading(false);
            return;
        }

        const rolePayload = {
            ...selectedRole,
            userType: userType,
            parentRoleId: selectedRole.parentRoleId || null // Convert empty string to null
        };

        console.log('Role payload to send:', rolePayload);
        console.log('API endpoint:', `${ADMIN_API_URL}roles/${selectedRole.id}`);

        try {
            const response = await fetch(`${ADMIN_API_URL}roles/${selectedRole.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `${token}`
                },
                body: JSON.stringify(rolePayload)
            });

            console.log('API response status:', response.status);

            if (!response.ok) {
                const errorData = await response.json();
                console.error('API error response:', errorData);
                throw new Error(errorData.message || "Failed to update role.");
            }

            const result = await response.json();
            console.log('Role updated successfully:', result);

            addNotification("Role updated successfully.", 'success');
            setShowEditRoleModal(false);
            setSelectedRole(null);
            fetchRoles(); // Refresh roles list
            fetchAllRolesForParent(); // Refresh parent roles list
        } catch (err) {
            console.error('Error updating role:', err);
            setError(err.message);
            addNotification(`Error: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRole = (roleId) => {
        // Just show confirmation modal first (no API call yet)
        setRoleToDelete(roleId);
        setDeleteErrorMessage(null);
        setNeedsReassignment(false);
        setUserCount(0);
        setAvailableRoles([]);
        setSelectedRoleId('');
        setShowDeleteConfirmModal(true);
    };

    const confirmDeleteRole = async () => {
        if (!roleToDelete) {
            setDeleteErrorMessage("No role selected for deletion.");
            setShowDeleteConfirmModal(true);
            return;
        }

        // If needs reassignment (second confirmation with role selected), check if role is selected
        if (needsReassignment && !selectedRoleId) {
            setDeleteErrorMessage("Please select a role to reassign users.");
            setShowDeleteConfirmModal(true);
            return;
        }

        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setDeleteErrorMessage("Authorization token not found. Please log in again.");
            setShowDeleteConfirmModal(true);
            setLoading(false);
            return;
        }

        try {
            // Prepare request body - include defaultRoleId if reassignment is needed (second call)
            const requestOptions = {
                method: "DELETE",
                headers: {
                    "Authorization": `${token}`,
                    "Content-Type": "application/json"
                }
            };

            // If users need reassignment (second confirmation), send defaultRoleId in body
            if (needsReassignment && selectedRoleId) {
                requestOptions.body = JSON.stringify({
                    defaultRoleId: selectedRoleId
                });
            }

            const response = await fetch(`${ADMIN_API_URL}roles/${roleToDelete}`, requestOptions);
            const result = await response.json();

            // Handle "Role not found" error (role already deleted)
            if (result.message && (result.message.includes("not found") || result.message.includes("Role not found"))) {
                // Role already deleted - show success and refresh
                addNotification("Role already deleted.", 'success');
                setShowDeleteConfirmModal(false);
                setRoleToDelete(null);
                setNeedsReassignment(false);
                setUserCount(0);
                setAvailableRoles([]);
                setSelectedRoleId('');
                fetchRoles(); // Refresh roles list
                fetchAllRolesForParent(); // Refresh parent roles list
                setLoading(false);
                return;
            }

            if (!response.ok) {
                console.error("Backend error data:", result);
                const errorMessage = result.message || "Failed to delete role.";
                setDeleteErrorMessage(errorMessage);
                setLoading(false);
                return;
            }

            // If this is first confirmation (needsReassignment is false), check response
            if (!needsReassignment) {
                // Check if users exist and need reassignment
                if (result.canDelete === false && result.userCount > 0) {
                    // Role has users - show modal with role selection
                    if (result.availableRoles && result.availableRoles.length > 0) {
                        setNeedsReassignment(true);
                        setUserCount(result.userCount);
                        setAvailableRoles(result.availableRoles);
                        // Keep modal open, just update the content
                        setLoading(false);
                    } else {
                        // No available roles for reassignment
                        setDeleteErrorMessage("No available roles for reassignment. Cannot delete this role.");
                        setLoading(false);
                    }
                } else if (result.message && result.message.toLowerCase().includes("deleted successfully")) {
                    // Role deleted successfully (no users)
                    addNotification(result.message || "Role deleted successfully.", 'success');
                    setShowDeleteConfirmModal(false);
                    setRoleToDelete(null);
                    setNeedsReassignment(false);
                    setUserCount(0);
                    setAvailableRoles([]);
                    setSelectedRoleId('');
                    fetchRoles(); // Refresh roles list
                    fetchAllRolesForParent(); // Refresh parent roles list
                    setLoading(false);
                } else {
                    // Unexpected response
                    setDeleteErrorMessage("Unexpected response from server.");
                    setLoading(false);
                }
            } else {
                // Second confirmation with role selected - role should be deleted now
                if (result.message && result.message.toLowerCase().includes("deleted successfully")) {
                    const successMessage = userCount > 0
                        ? `Role deleted successfully. ${userCount} user(s) reassigned.`
                        : result.message || "Role deleted successfully.";
                    
                    addNotification(successMessage, 'success');
                    setShowDeleteConfirmModal(false);
                    setRoleToDelete(null);
                    setNeedsReassignment(false);
                    setUserCount(0);
                    setAvailableRoles([]);
                    setSelectedRoleId('');
                    fetchRoles(); // Refresh roles list
                    fetchAllRolesForParent(); // Refresh parent roles list
                    setLoading(false);
                } else {
                    // Unexpected response
                    setDeleteErrorMessage("Unexpected response from server.");
                    setLoading(false);
                }
            }
        } catch (err) {
            setDeleteErrorMessage(`Error: ${err.message}`);
            setShowDeleteConfirmModal(true);
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleRecordsPerPageChange = (e) => {
        setRecordsPerPage(Number(e.target.value));
        setCurrentPage(1); // Reset to first page
    };

    const formatDateForDisplay = (isoDateString) => {
        if (!isoDateString) return 'N/A';
        const date = new Date(isoDateString);
        return date.toLocaleDateString(); // e.g., 6/13/2025
    };

    if (loading) {
        return <div className="text-center p-4">Loading roles...</div>;
    }

    if (error) {
        return <div className="text-center p-4 text-danger">Error: {error}</div>;
    }

    return (
        <>
            <Header />
            <div id="wrapper" style={{ paddingTop: '70px' }}>
                <div className="content">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    <h2 className="font-light m-b-xs">Role Management</h2>
                                        <button
                                        className="btn btn-primary m-b-md"
                                        onClick={() => {
                                            setNewRole({
                                                roleName: '',
                                                roleDescription: '',
                                                status: 'Active',
                                                userType: 0,
                                                parentRoleId: '',
                                                assignedUserIds: [],
                                                permissions: []
                                            });
                                            setShowCreateRoleModal(true);
                                        }}
                                    >
                                        Create Role
                                    </button>

                                    <div className="table-responsive">
                                        <table className="table table-striped table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Role Name</th>
                                                    <th>Description</th>
                                                    <th>Permissions</th>
                                                    <th>Status</th>
                                                    <th>User Type</th>
                                                    <th>Assigned Users</th>
                                                    <th>Created At</th>
                                                    <th>Last Updated</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {roles.length > 0 ? (
                                                    roles.map((role) => (
                                                        <tr key={role.id}>
                                                            <td>{role.roleName || 'N/A'}</td>
                                                            <td>{role.roleDescription || 'N/A'}</td>
                                                            <td>
                                                                {role.permissions && role.permissions.length > 0 ? (
                                                                    role.permissions.join(', ')
                                                                ) : 'N/A'}
                                                            </td>
                                                            <td>{role.status || 'N/A'}</td>
                                                            <td>{role.userTypeLabel || (role.userType !== undefined ? `Type ${role.userType}` : 'N/A')}</td>
                                                            <td>
                                                                {role.assignedUsers && role.assignedUsers.length > 0 ? (
                                                                    role.assignedUsers.map(user => user.name).join(', ')
                                                                ) : 'N/A'}
                                                            </td>
                                                            <td>{formatDateForDisplay(role.createdAt)}</td>
                                                            <td>{formatDateForDisplay(role.updatedAt)}</td>
                                                            <td>
                                                                <div className="dropdown">
                                                                    <button
                                                                        className="btn btn-sm dropdown-toggle"
                                                                        type="button"
                                                                        id={`dropdownMenuButton-${role.id}`}
                                                                        data-toggle="dropdown"
                                                                        aria-haspopup="true"
                                                                        aria-expanded="false"
                                                                        style={{ backgroundColor: '#34495e', color: '#ffffff' }}
                                                                    >
                                                                        Actions <span className="caret"></span>
                                                                    </button>
                                                                    <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${role.id}`}>
                                                                        <li>
                                                                            <a
                                                                                className="dropdown-item"
                                                                                href="#"
                                                                                onClick={() => {
                                                                                    // Ensure userType has a default value if undefined
                                                                                    setSelectedRole({
                                                                                        ...role,
                                                                                        userType: role.userType !== undefined ? role.userType : 0
                                                                                    });
                                                                                    setShowEditRoleModal(true);
                                                                                }}
                                                                            >
                                                                                Edit
                                                                            </a>
                                                                        </li>
                                                                        <li>
                                                                            <a
                                                                                className="dropdown-item text-danger"
                                                                                href="#"
                                                                                onClick={() => handleDeleteRole(role.id)}
                                                                            >
                                                                                Delete
                                                                            </a>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="10" className="text-center">No roles found.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="text-right m-t-md">
                                        <label htmlFor="recordsPerPage" className="m-r-sm">Records per page:</label>
                                        <select
                                            id="recordsPerPage"
                                            className="form-control input-sm"
                                            style={{ display: 'inline-block', width: 'unset' }}
                                            value={recordsPerPage}
                                            onChange={handleRecordsPerPageChange}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <button
                                            className="btn btn-default m-r-sm m-l-sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                        <span>Page {currentPage} of {totalPages}</span>
                                        <button
                                            className="btn btn-default m-l-sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>

            {/* Create Role Modal */}
            {showCreateRoleModal && (
                <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg" style={{ maxWidth: '700px' }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">Create Role</h4>
                                <button type="button" className="close" onClick={() => setShowCreateRoleModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                                <form>
                                    <div className="form-group">
                                        <label>Role Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newRole.roleName}
                                            onChange={(e) => setNewRole({ ...newRole, roleName: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Role Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={newRole.roleDescription}
                                            onChange={(e) => setNewRole({ ...newRole, roleDescription: e.target.value })}
                                        ></textarea>
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select
                                            className="form-control"
                                            value={newRole.status}
                                            onChange={(e) => setNewRole({ ...newRole, status: e.target.value })}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>User Type *</label>
                                        <select
                                            className="form-control"
                                            value={newRole.userType}
                                            onChange={(e) => setNewRole({ ...newRole, userType: parseInt(e.target.value) })}
                                            required
                                        >
                                            {USER_TYPE_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Parent Role</label>
                                        <select
                                            className="form-control"
                                            value={newRole.parentRoleId || ''}
                                            onChange={(e) => setNewRole({ ...newRole, parentRoleId: e.target.value })}
                                        >
                                            <option value="">No Parent (Top Level)</option>
                                            {allRolesForParent
                                                .filter(role => role.id !== newRole.id) // Exclude self (when editing)
                                                .map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.roleName}
                                                    </option>
                                                ))}
                                        </select>
                                        <small className="text-muted">
                                            Select parent role for hierarchy. Leave empty for top-level roles.
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label>Permissions</label>
                                        {permissions.map((perm) => (
                                            <div key={perm.id} className="checkbox">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        value={perm.permissionName}
                                                        checked={newRole.permissions.includes(perm.permissionName)}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setNewRole(prev => ({
                                                                ...prev,
                                                                permissions: checked
                                                                    ? [...prev.permissions, perm.permissionName]
                                                                    : prev.permissions.filter(p => p !== perm.permissionName)
                                                            }));
                                                        }}
                                                    /> {perm.permissionName}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Assigned Users - Placeholder for now, as no user fetching API is provided */}
                                    <div className="form-group">
                                        <label>Assigned Users (Enter Emails separated by comma)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={newRole.assignedUserIds.join(', ')}
                                            onChange={(e) => setNewRole({ ...newRole, assignedUserIds: e.target.value.split(',').map(email => email.trim()).filter(email => email) })}
                                            placeholder="e.g., user1@example.com, user2@example.com"
                                        />
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" onClick={() => setShowCreateRoleModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleCreateRole}>Create</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Role Modal */}
            {showEditRoleModal && selectedRole && (
                <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg" style={{ maxWidth: '700px' }}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h4 className="modal-title">Edit Role: {selectedRole.roleName}</h4>
                                <button type="button" className="close" onClick={() => setShowEditRoleModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body" style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
                                <form>
                                    <div className="form-group">
                                        <label>Role Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={selectedRole.roleName}
                                            onChange={(e) => setSelectedRole({ ...selectedRole, roleName: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Role Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={selectedRole.roleDescription}
                                            onChange={(e) => setSelectedRole({ ...selectedRole, roleDescription: e.target.value })}
                                        ></textarea>
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select
                                            className="form-control"
                                            value={selectedRole.status}
                                            onChange={(e) => setSelectedRole({ ...selectedRole, status: e.target.value })}
                                        >
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>User Type *</label>
                                        <select
                                            className="form-control"
                                            value={selectedRole.userType !== undefined ? selectedRole.userType : 0}
                                            onChange={(e) => setSelectedRole({ ...selectedRole, userType: parseInt(e.target.value) })}
                                            required
                                        >
                                            {USER_TYPE_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Parent Role</label>
                                        <select
                                            className="form-control"
                                            value={selectedRole.parentRoleId || ''}
                                            onChange={(e) => setSelectedRole({ ...selectedRole, parentRoleId: e.target.value })}
                                        >
                                            <option value="">No Parent (Top Level)</option>
                                            {allRolesForParent
                                                .filter(role => role.id !== selectedRole.id) // Exclude self (can't be own parent)
                                                .map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.roleName}
                                                    </option>
                                                ))}
                                        </select>
                                        <small className="text-muted">
                                            Select parent role for hierarchy. Leave empty for top-level roles.
                                        </small>
                                    </div>
                                    <div className="form-group">
                                        <label>Permissions</label>
                                        {permissions.map((perm) => (
                                            <div key={perm.id} className="checkbox">
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        value={perm.permissionName}
                                                        checked={selectedRole.permissions.includes(perm.permissionName)}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setSelectedRole(prev => ({
                                                                ...prev,
                                                                permissions: checked
                                                                    ? [...(prev.permissions || []), perm.permissionName]
                                                                    : (prev.permissions || []).filter(p => p !== perm.permissionName)
                                                            }));
                                                        }}
                                                    /> {perm.permissionName}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Assigned Users - Placeholder for now, as no user fetching API is provided */}
                                    <div className="form-group">
                                        <label>Assigned Users (Enter Emails separated by comma)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={(selectedRole.assignedUserIds || []).join(', ')}
                                            onChange={(e) => setSelectedRole({ ...selectedRole, assignedUserIds: e.target.value.split(',').map(email => email.trim()).filter(email => email) })}
                                            placeholder="e.g., user1@example.com, user2@example.com"
                                        />
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-default" onClick={() => setShowEditRoleModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleUpdateRole}>Update</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmModal && (
                <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog" style={{ maxWidth: needsReassignment ? '550px' : '500px', marginTop: '10%' }}>
                        <div className="modal-content" style={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                            <div className="modal-header" style={{ padding: '20px 25px', borderBottom: '1px solid #e5e5e5' }}>
                                <h4 className="modal-title" style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>
                                    {deleteErrorMessage ? "Error" : "Confirm Deletion"}
                                </h4>
                                <button 
                                    type="button" 
                                    className="close" 
                                    onClick={() => {
                                        setShowDeleteConfirmModal(false);
                                        setDeleteErrorMessage(null);
                                        setNeedsReassignment(false);
                                        setUserCount(0);
                                        setAvailableRoles([]);
                                        setSelectedRoleId('');
                                    }}
                                    style={{ fontSize: '28px', opacity: 0.6, padding: '0', margin: '0' }}
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="modal-body" style={{ padding: '30px 25px', fontSize: '16px', lineHeight: '1.6' }}>
                                {deleteErrorMessage ? (
                                    <p className="text-danger" style={{ margin: 0, fontSize: '15px' }}>{deleteErrorMessage}</p>
                                ) : needsReassignment ? (
                                    <>
                                        <p style={{ margin: '0 0 15px 0', color: '#333' }}>This role has <strong>{userCount}</strong> user(s) assigned.</p>
                                        <p style={{ margin: '0 0 15px 0', color: '#333' }}>Select role to reassign them to:</p>
                                        {availableRoles.length > 0 ? (
                                            <div className="form-group" style={{ marginBottom: 0 }}>
                                                <select
                                                    className="form-control"
                                                    value={selectedRoleId}
                                                    onChange={(e) => setSelectedRoleId(e.target.value)}
                                                    style={{ fontSize: '14px', padding: '8px 12px' }}
                                                >
                                                    <option value="">-- Select Role --</option>
                                                    {availableRoles.map(role => (
                                                        <option key={role.id} value={role.id}>
                                                            {role.roleName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <p className="text-danger" style={{ margin: 0, fontSize: '15px' }}>No available roles for reassignment.</p>
                                        )}
                                    </>
                                ) : (
                                    <p style={{ margin: 0, color: '#333' }}>Are you sure you want to delete this role?</p>
                                )}
                            </div>
                            <div className="modal-footer" style={{ padding: '20px 25px', borderTop: '1px solid #e5e5e5' }}>
                                {deleteErrorMessage ? (
                                    <button 
                                        type="button" 
                                        className="btn btn-default" 
                                        onClick={() => {
                                            setShowDeleteConfirmModal(false);
                                            setDeleteErrorMessage(null);
                                            setNeedsReassignment(false);
                                            setUserCount(0);
                                            setAvailableRoles([]);
                                            setSelectedRoleId('');
                                        }}
                                        style={{ padding: '8px 20px', fontSize: '14px' }}
                                    >
                                        Close
                                    </button>
                                ) : needsReassignment ? (
                                    <>
                                        <button 
                                            type="button" 
                                            className="btn btn-default" 
                                            onClick={() => {
                                                setShowDeleteConfirmModal(false);
                                                setDeleteErrorMessage(null);
                                                setNeedsReassignment(false);
                                                setUserCount(0);
                                                setAvailableRoles([]);
                                                setSelectedRoleId('');
                                            }}
                                            style={{ padding: '8px 20px', fontSize: '14px', marginRight: '10px' }}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="button" 
                                            className="btn btn-primary" 
                                            onClick={confirmDeleteRole}
                                            disabled={!selectedRoleId || availableRoles.length === 0}
                                            style={{ padding: '8px 25px', fontSize: '14px', fontWeight: '500' }}
                                        >
                                            Delete & Reassign
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            type="button" 
                                            className="btn btn-default" 
                                            onClick={() => {
                                                setShowDeleteConfirmModal(false);
                                                setDeleteErrorMessage(null);
                                            }}
                                            style={{ padding: '8px 20px', fontSize: '14px', marginRight: '10px' }}
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="button" 
                                            className="btn btn-primary" 
                                            onClick={confirmDeleteRole}
                                            style={{ padding: '8px 25px', fontSize: '14px', fontWeight: '500' }}
                                        >
                                            OK
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default RoleManagementPage; 